import StarterKit from "@tiptap/starter-kit";
import HardBreak from "@tiptap/extension-hard-break";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown, type MarkdownNodeSpec } from "tiptap-markdown";

/**
 * NoteEditor（ノートの WYSIWYG エディタ）が使う TipTap 拡張一式。
 *
 * コンポーネントから切り出しているのは、Markdown 往復（読み込み → 保存）の
 * 冪等性テスト（NoteEditor.markdown.test.ts）が実物とまったく同じ構成で
 * エディタを組み立てられるようにするため。ここを変更したら往復テストも通ること。
 */

/**
 * Shift+Enter（hardBreak）を行末 `\`（強制改行記法）ではなく素の改行として
 * Markdown 化する。エディタは Markdown.configure({ breaks: true }) で素の
 * 改行を hardBreak として読むため往復は安定し、閲覧側も breaks 表示なので
 * 見た目が一致する。テーブルのセル内だけは改行を Markdown で表現できない
 * ため、既定実装と同じ `<br>` フォールバックを維持する。
 * 段落末尾の hardBreak を出力しない挙動（後続に通常ノードが無い場合は
 * スキップ）も既定実装のまま。
 */
const plainBreakMarkdownSpec: MarkdownNodeSpec = {
  serialize(state, node, parent, index) {
    for (let i = index + 1; i < parent.childCount; i += 1) {
      if (parent.child(i).type !== node.type) {
        const inTable = (state as unknown as { inTable?: boolean }).inTable;
        state.write(inTable ? "<br>" : "\n");
        return;
      }
    }
  },
};

export const PlainBreakHardBreak = HardBreak.extend({
  addStorage() {
    return { markdown: plainBreakMarkdownSpec };
  },
});

/**
 * 拡張一式を組み立てて返す。
 *
 * Image を inline: true にしているのが重要な設定。TipTap の Image は既定で
 * block ノードだが、tiptap-markdown（prosemirror-markdown）の image
 * シリアライザは CommonMark と同じ inline 前提のため、block のまま往復すると
 * 「読み込みで <p> から画像が引き抜かれる」「書き出しで画像の後にブロック
 * 終端（空行）が書かれない」の両側から、画像前後の改行・空行が保存のたびに
 * 増減していた（例: `![a](u)\n\ntext` → `![a](u)text`、`![a](u)\n![b](v)` は
 * 保存のたびに空行が増えたり消えたりする）。inline にすると Markdown の
 * 画像モデルと一致して往復が冪等になる。見た目は閲覧側（MarkdownContainer の
 * `& img` 指定）と同じ CSS を共有しているため変わらない。
 */
export const buildNoteEditorExtensions = () => [
  StarterKit.configure({
    // ツールバーは H1〜H3 のみだが、貼り付けた Markdown の #### 〜
    // ###### が見出しとして保持されるようスキーマは 6 段まで許可する
    heading: { levels: [1, 2, 3, 4, 5, 6] },
    // hardBreak は素の改行で保存する拡張（PlainBreakHardBreak）に差し替え
    hardBreak: false,
  }),
  PlainBreakHardBreak,
  Link.configure({ openOnClick: false }),
  Image.configure({ inline: true }),
  TaskList,
  TaskItem.configure({ nested: true }),
  Table.configure({ resizable: false }),
  TableRow,
  TableCell,
  TableHeader,
  Placeholder.configure({
    placeholder:
      "入力してください。[[ノートタイトル]] で他のノートへリンクできます",
  }),
  Markdown.configure({
    html: true,
    linkify: true,
    breaks: true,
    transformPastedText: true,
  }),
];
